import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { DROPZONE_SELECTED } from '../shared/events/drag-drop.events';
import { DraggableComponent } from '../draggable/draggable.component';
import { DragDropValueEvent } from '../shared/events/drag-drop-value.event';
import { DragDropService } from '../shared/services/drag-drop.service';
import { DropZoneService } from './drop-zone.service';
import { dropZoneStyles, tag } from './drop-zone.styles';

@customElement(tag)
export class DropZoneComponent extends TmElement {
  /** Decorated properties */

  // collapse items when dragged outside
  @property({ type: Boolean, noAccessor: true })
  set collapse(collapse: boolean) {
    this.dropzoneService.setCollapse(collapse);
  }

  @property({ type: Boolean, noAccessor: true })
  set useDropper(useDropper: boolean) {
    this.dropzoneService.useDropper(useDropper);
  }

  @property({ type: Boolean, noAccessor: true })
  set disableOutsideBounceBack(value: boolean) {
    this.dropzoneService.setDisableOutsideBounceBack(value);
  }

  @property({ type: Boolean, noAccessor: true, reflect: true })
  set singleValue(value: boolean) {
    this.dropzoneService.setSingleValue(value);
  }

  get singleValue() {
    return this.dropzoneService.getSingleValue();
  }

  @queryAssignedElements({ selector: 'a-draggable' })
  draggableComponents!: Array<DraggableComponent>;

  get allDraggableComponents() {
    const slottedDraggables = this.slottedChildren?.filter(
      (child) => child instanceof DraggableComponent,
    ) as Array<DraggableComponent>;

    if (!slottedDraggables) {
      return this.draggableComponents;
    }

    return [...new Set([...this.draggableComponents, ...slottedDraggables])];
  }

  /**
   * Sadly this property is needed.
   * Somehow chromium does have some trouble when you manipulate the dom within the web component and outside the component.
   * When you drag an external draggable to a dropzone its last position it is prepended instead of appended.
   */
  @property({ type: Boolean, noAccessor: true })
  set contentManagedExternally(value: boolean) {
    this.dropzoneService.setContentManagedExternally(value);
  }

  @property({ type: Boolean, reflect: true })
  disabled?: boolean = false;

  @property({ type: Boolean })
  restrictDraggingOutside = false;

  @property({ type: Boolean, reflect: true })
  horizontal?: boolean = false;

  @property({ type: Number })
  marginOfError = 0;

  /** Properties */

  #redrawTimeout?: number;

  #mutationObserver?: MutationObserver;

  #dragDropService = new DragDropService();

  dropzoneService: DropZoneService;

  constructor() {
    super();
    this.dropzoneService = new DropZoneService(this, this.#dragDropService);
    this.#dragDropService.registerDropZone(this);
  }

  static override styles = dropZoneStyles;

  /** Component overrides */
  override connectedCallback() {
    super.connectedCallback();

    if (!this.disabled) {
      this.setAttribute('tabindex', '0');
    }

    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('mousedown', this.#handleClick);

    this.#mutationObserver = new MutationObserver(
      this.#handleMutation.bind(this),
    );
    this.#mutationObserver.observe(this, { childList: true });
  }

  #handleKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
      this.dispatchGlobalEvent({
        eventName: DROPZONE_SELECTED,
        element: (event.target as HTMLElement) ?? this,
        detail: this,
        bubbles: true,
      });
    }
  }

  #handleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dispatchGlobalEvent({
      eventName: DROPZONE_SELECTED,
      element: (event.target as HTMLElement) ?? this,
      detail: this,
      bubbles: true,
    });
  }

  setSelected(selected: boolean) {
    if (selected) {
      this.classList.add('selected');
    } else {
      this.classList.remove('selected');
    }
  }

  setColliding(colliding: boolean) {
    if (colliding) {
      this.classList.add('colliding');
    } else {
      this.classList.remove('colliding');
    }
  }

  #handleMutation(mutations: Array<MutationRecord>) {
    const shouldInitializeDraggables: boolean = mutations.reduce(
      (shouldReworkDraggables, mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].reduce(
          (shouldReworkDraggables, node) => {
            if (DraggableComponent.isDraggableComponent(node)) {
              return true;
            }

            return shouldReworkDraggables;
          },
          shouldReworkDraggables,
        ),
      false,
    );

    if (shouldInitializeDraggables) {
      // Needs to be place on another thread to prevent bugs between mutations, setting index and layout invalidation
      setTimeout(() => {
        this.dropzoneService.setDraggableComponents(
          this.allDraggableComponents,
        );
        this.dropzoneService.layoutInvalidated(false);
      });
    }
  }

  protected override firstUpdated() {
    this.dropzoneService.initialize(this.allDraggableComponents);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#dragDropService.unregisterDropZone(this);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('mousedown', this.#handleClick);
  }

  override render() {
    return html`<div class="clickable-area">
      <div class="container">
        <slot></slot>
      </div>
    </div>`;
  }

  /** public methods */

  redraw(reappend = true) {
    if (this.#redrawTimeout) {
      clearTimeout(this.#redrawTimeout);
    }

    if (reappend) {
      this.#reappendComponents();
    } else {
      this.dropzoneService.setIndexDraggables(this.allDraggableComponents);
    }
  }

  broadCastEvent(event: string, value: unknown) {
    this.dispatchEvent(new DragDropValueEvent(event, value));
  }

  /** private methods */
  #reappendComponents() {
    const draggableComponentsSorted = [...this.allDraggableComponents].sort(
      (a, b) => {
        return a.index > b.index ? 1 : -1;
      },
    );

    draggableComponentsSorted.forEach((content) => {
      this.append(content);
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-drop-zone': DropZoneComponent;
  }
}
